import * as React from 'react';
import {
  EPageTypes,
  EPlatformTypes,
  SimpleCall,
  ISimpleCallProps,
  prepareSimpleCallPropsByRealtyUserId,
} from '@cian/frontend-newbuilding-call-component';
import { useDispatch, useSelector } from 'react-redux';

import { markAsOpen } from './utils/markAsOpen';
import { setOpenedPhones } from '../../actions/openedPhones';
import {
  newbuildingIdSelector,
  buildersSelector,
  sellersSelector,
  sellersWithValidPhonesSelector,
  buildersWithValidPhonesSelector,
} from '../../selectors';
import { getOpenedPhones } from '../../selectors/phones/getOpenedPhones';
import { INewbuildingBuilder, ISeller } from '../../types/newbuilding/builder';
import { useApplicationContext } from '../../utils/applicationContext';
import { isLeadFactory } from '../../utils/agent';

interface IShowPhoneParams {
  realtyUserId: number;
  phone: string;
}

type TSimpleCallProps = Pick<
  ISimpleCallProps,
  'onOpenContactsClick' | 'openContactsStyles' | 'showPhoneStyles' | 'SinglePhoneComponent'
>;

export interface ICallButtonProps extends TSimpleCallProps {
  placeType: string;
  preserveOpen?: boolean;
  realtyUserIdToDisplayPhone?: number;
  onShowPhoneClick?(params: IShowPhoneParams): void;
}

export const CallButton = ({
  placeType,
  onShowPhoneClick,
  onOpenContactsClick,
  openContactsStyles,
  showPhoneStyles,
  SinglePhoneComponent,
  preserveOpen = true,
  realtyUserIdToDisplayPhone,
}: ICallButtonProps) => {
  const { httpApi, logger } = useApplicationContext();
  const newbuildingId = useSelector(newbuildingIdSelector);
  const opened = useSelector(getOpenedPhones)[placeType];
  const builders = useSelector(buildersSelector);
  const buildersWithValidPhones = useSelector(buildersWithValidPhonesSelector);
  const sellers = useSelector(sellersSelector);
  const sellersWithValidPhones = useSelector(sellersWithValidPhonesSelector);
  const dispatch = useDispatch();

  const callBuilders = React.useMemo(
    () =>
      Array.isArray(opened) ? builders.map(seller => markAsOpen<INewbuildingBuilder>({ seller, opened })) : builders,
    [builders, opened],
  );
  const callSellers = React.useMemo(
    () => (Array.isArray(opened) ? sellers.map(seller => markAsOpen<ISeller>({ seller, opened })) : sellers),
    [sellers, opened],
  );

  const handleShowPhoneClick = React.useCallback(
    (params: IShowPhoneParams) => {
      const { realtyUserId, phone } = params;
      const newOpened = [{ realtyUserId, phone }, ...(opened || [])];

      if (preserveOpen) {
        dispatch(setOpenedPhones({ [placeType]: newOpened }));
      }

      if (onShowPhoneClick) {
        onShowPhoneClick(params);
      }
    },
    [newbuildingId, opened, onShowPhoneClick, placeType, dispatch, preserveOpen],
  );

  const pageType = EPageTypes.NewbuildingCard;
  const platformType = EPlatformTypes.WebDesktop;

  const userProps = realtyUserIdToDisplayPhone
    ? prepareSimpleCallPropsByRealtyUserId(realtyUserIdToDisplayPhone, builders, sellers)
    : { builders: callBuilders, sellers: callSellers };

  const isLeadFactorySeller = sellersWithValidPhones.some(isLeadFactory);
  const singlePhoneButtonText = isLeadFactorySeller ? 'Позвонить консультанту' : undefined;
  const forcedShowModal =
    buildersWithValidPhones.length === 0 && sellersWithValidPhones.length === 1 && !isLeadFactorySeller;

  return (
    <SimpleCall
      SinglePhoneComponent={SinglePhoneComponent}
      onOpenContactsClick={onOpenContactsClick}
      onShowPhoneClick={handleShowPhoneClick}
      openContactsStyles={openContactsStyles}
      showPhoneStyles={showPhoneStyles}
      httpApi={httpApi}
      logger={logger}
      pageType={pageType}
      platformType={platformType}
      placeType={placeType}
      singlePhoneButtonText={singlePhoneButtonText}
      forcedShowModal={forcedShowModal}
      {...userProps}
    />
  );
};

CallButton.displayName = 'CallButton';
